<script setup lang="ts">
import { AsyncThemeConfig } from "types/index";
import { useData } from "vitepress";
import { ref, onMounted } from "vue";
import { formatDate } from "../utils/client";
import pkg from "../package.json";

const { theme } = useData<AsyncThemeConfig>();
const day = ref<number>(0);

const showDateTime = (date: string) => {
  const BirthDay = new Date(date);
  const today = new Date();
  const timeold = today.getTime() - BirthDay.getTime();
  const msPerDay = 24 * 60 * 60 * 1000;
  const count = Math.floor(timeold / msPerDay);
  day.value = count;
};

onMounted(() => {
  theme.value.footer?.liveTime?.startTime && showDateTime(theme.value.footer?.liveTime?.startTime);

  const container = document.querySelector(".bottom-buttons-container");
  let isDown = false;
  let startX: number;
  let scrollLeft: number;

  container?.addEventListener("mousedown", e => {
    isDown = true;
    container.classList.add("active");
    startX = e.pageX - container.offsetLeft;
    scrollLeft = container.scrollLeft;
  });

  container?.addEventListener("mouseleave", () => {
    isDown = false;
    container.classList.remove("active");
  });

  container?.addEventListener("mouseup", () => {
    isDown = false;
    container.classList.remove("active");
  });

  container?.addEventListener("mousemove", e => {
    if (!isDown) return;
    e.preventDefault();
    const x = e.pageX - container.offsetLeft;
    const walk = (x - startX) * 3; // 滑动速度
    container.scrollLeft = scrollLeft - walk;
  });

  // 检测是否存在水平滚动条
  const checkScrollbar = () => {
    if (container.scrollWidth > container.clientWidth) {
      container.style.justifyContent = "flex-start"; // 存在滚动条时左对齐
    } else {
      container.style.justifyContent = "center"; // 不存在滚动条时居中
    }
  };

  checkScrollbar();
  window.addEventListener("resize", checkScrollbar); // 窗口大小变化时重新检测
});
</script>

<template>
  <div :class="['bottom-buttons-container']">
    <a class="bottom-button no-border"
       href="https://2024.igem.wiki/hkust-gz/human-practices">
      <img src="https://static.igem.wiki/teams/5280/foot/human-practices-1.png"
           alt="Icon" />
      <span class="button-label">Human Practices</span>
    </a>
    <a class="bottom-button no-border"
       href="https://2024.igem.wiki/hkust-gz/team">
      <img src="https://static.igem.wiki/teams/5280/foot/team-1.png"
           alt="Icon" />
      <span class="button-label">Team</span>
    </a>
    <a class="bottom-button no-border"
       href="https://2024.igem.wiki/hkust-gz/experiments">
      <img src="https://static.igem.wiki/teams/5280/foot/experiment-1.png"
           alt="Icon" />
      <span class="button-label">Experiments</span>
    </a>
    <a class="bottom-button no-border"
       href="https://2024.igem.wiki/hkust-gz/description">
      <img src="https://static.igem.wiki/teams/5280/foot/description-1.png"
           alt="Icon" />
      <span class="button-label">Description</span>
    </a>
    <a class="bottom-button no-border"
       href="https://2024.igem.wiki/hkust-gz/engineering">
      <img src="https://static.igem.wiki/teams/5280/foot/enginering-1.png"
           alt="Icon" />
      <span class="button-label">Engineering</span>
    </a>
    <a class="bottom-button no-border"
       href="https://2024.igem.wiki/hkust-gz/safety">
      <img src="https://static.igem.wiki/teams/5280/foot/safety-1.png"
           alt="Icon" />
      <span class="button-label">Safety</span>
    </a>
  </div>
  <footer class="trm-footer trm-scroll-animation">
    <div class="trm-footer-item">
      <span>© 2024 - Content on this site is licensed under a <a href="https://creativecommons.org/licenses/by/4.0/"
           target="_blank"
           rel="noopener">Creative Commons Attribution 4.0 International license</a>.</span>
    </div>
    <div class="trm-footer-item">
      <span>The repository used to create this website is available at <a href="https://gitlab.igem.org/2024/hkust-gz"
           target="_blank"
           rel="noopener">gitlab.igem.org/2024/hkust-gz</a>.</span>
    </div>
    <div class="trm-footer-item">
      <span>Contact us at <a href="mailto:hkustgz.igem@outlook.com">hkustgz.igem@outlook.com</a></span>
    </div>
  </footer>
</template>

<style lang="less">
.bottom-buttons-container {
  display: flex;
  justify-content: center; /* 默认居中 */
  align-items: center;
  padding: 20px 0;
  overflow-x: auto; /* 允许水平滚动 */
  scroll-snap-type: x mandatory; /* 启用滚动捕捉 */
}

.bottom-buttons-container .bottom-button {
  scroll-snap-align: start; /* 每个按钮捕捉到开始位置 */
  margin: 0 10px;
  padding: 10px;
  background-color: var(--bottom-buttons-container-bg); /* Light mode background */
  color: var(--bottom-buttons-container-color); /* Light mode text */
  text-decoration: none;
  border: var(--bottom-buttons-container-border); /* Light mode border */
  border-radius: 5px;
  transition: background-color 0.3s ease, box-shadow 0.3s ease, filter 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 128px; /* fixed width */
  height: 128px; /* fixed height to maintain uniformity */
  position: relative;
}

.bottom-buttons-container .bottom-button img {
  width: 128px; /* smaller icon size */
  height: 128px;
  margin-top: -15px; /* 上移 15px */
  filter: blur(0); /* 初始无模糊 */
  transition: filter 0.3s ease;
}

.bottom-buttons-container .bottom-button span.button-label {
  position: absolute;
  bottom: 0;
  background-color: var(--bottom-buttons-container-bg, 0.8); /*半透明背景*/
  padding: 5px;
  font-size: 14px;
  text-align: center;
  width: 100%;
  opacity: 0; /* 初始不可见 */
  transition: opacity 0.3s ease;
}

/* 选择性去掉某些按钮的边框并添加悬停效果 */
.bottom-buttons-container .bottom-button.no-border {
  border: none;
}

.bottom-buttons-container .bottom-button.no-border:hover {
  background-color: var(--bottom-buttons-container-hover-bg); /* 悬停时的背景颜色 */
  box-shadow: none; /* 去掉悬停时的框阴影 */
}

.bottom-buttons-container .bottom-button:hover img {
  filter: blur(1px); /* 悬停时图片模糊 1px */
}

.bottom-buttons-container .bottom-button:hover span.button-label {
  opacity: 1; /* 悬停时显示类别文本 */
}

.bottom-buttons-container.active {
  cursor: grabbing;
  cursor: -webkit-grabbing;
}

.trm-footer {
  background-color: var(--theme-bg-color, #fcfcfe);
  box-shadow: var(--box-shadow2, 0 2px 4px -2px rgba(0, 0, 0, 0.15));
  border-radius: var(--card-border-radius, 10px);
  text-align: center;
  width: 100%;
  padding: 20px;

  .trm-footer-item {
    color: var(--body-color, #7b7b7d);
    min-height: 1.6rem;
    line-height: 1.6rem;
    font-size: 0.9rem;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;

    a {
      color: var(--primary, #006fa2);
      transition: all 0.4s ease-in-out;

      &:hover {
        color: var(--primary-weak, #006fa2);
      }
    }
  }

  .footer-separator,
  #since {
    margin: 0 0.5rem;
  }

  .footer-separator::before {
    content: attr(data-separator);
  }

  @media (max-width: 768px) {
    .footer-separator::before {
      content: "  ";
    }
  }
}
</style>